import { IconCreditCard, IconShieldCheck } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";

interface Props {
  /** Drives the copy, not the visuals. */
  stage: "confirming" | "done";
}

/**
 * Full-screen overlay shown while Stripe is confirming the payment.
 *
 *   1. confirming — Stripe round-trip in progress.
 *   2. done       — brief success flourish before navigating.
 *
 * The Odoo SO is confirmed server-side by the Stripe webhook, not here, so
 * there's no longer a "creating-order" stage on the FE. The confirmation
 * page handles the brief "we're processing your order" window if the user
 * lands before the webhook has caught up.
 */
export function PaymentProcessingOverlay({ stage }: Props) {
  const isDone = stage === "done";
  const heading = isDone ? "Payment confirmed" : "Processing your payment…";
  const sub = isDone
    ? "Redirecting you to your order summary."
    : "Securely contacting your bank. Please don't close this tab.";

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-textDark/40 backdrop-blur-sm"
    >
      <div className="flex w-[min(440px,calc(100vw-32px))] flex-col items-center gap-6 rounded-3xl bg-bgWhite p-8 shadow-[0_24px_64px_rgba(16,24,40,0.18)]">
        <div className="relative flex h-28 w-28 items-center justify-center">
          {!isDone && (
            <span
              className="absolute inset-0 rounded-full"
              style={{
                background: "conic-gradient(from 0deg, transparent 0deg, var(--color-accent-cyan-700, #05A7B8) 280deg, transparent 360deg)",
                animation: "spin 1.4s linear infinite",
                WebkitMask: "radial-gradient(circle, transparent 46%, black 47%)",
                mask: "radial-gradient(circle, transparent 46%, black 47%)",
              }}
            />
          )}
          <span
            className={`absolute inset-3 rounded-full ${
              isDone ? "bg-accentGreenTintBg" : "bg-accentCyanTint"
            } ${isDone ? "" : "animate-ping"} opacity-70`}
          />
          <div
            className={`relative flex h-16 w-16 items-center justify-center rounded-2xl ${
              isDone ? "bg-accentGreenDark text-white" : "bg-bgWhite text-accentCyan700"
            } shadow-[0_8px_24px_rgba(5,167,184,0.25)]`}
          >
            {isDone ? <IconShieldCheck size={30} /> : <IconCreditCard size={30} />}
          </div>
        </div>

        <div className="flex flex-col items-center gap-1 text-center">
          <Heading variant="h3" tone="dark">{heading}</Heading>
          <Text variant="bodySm" tone="muted">{sub}</Text>
        </div>

        <div className="flex w-full items-center gap-3">
          {(["confirming", "done"] as const).map((s, i) => {
            const reached = s === "confirming" || (s === "done" && isDone);
            return (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  reached ? "bg-accentCyan700" : "bg-borderDefault"
                }`}
                aria-hidden
                data-step={i}
              />
            );
          })}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
